/**
 * Zustand store pro schopnosti a presety Foscam kamer.
 *
 * `caps` se plní z `/api/foscam/caps` při initu a živě přes WS `foscam:caps`.
 * `presets` se lazy-loaduje per kamera (getFoscamPresets) při otevření ovládání.
 */

import { create } from "zustand";
import { getFoscamCaps } from "@client/api/foscam";
import type { AimProgress } from "@globals/foscam/types";

/**
 * Stav míření pro jednu kameru.
 *
 * `target` drží klient (server ho v průběžných zprávách neposílá) — je to místo, kam
 * uživatel klikl, a kreslí se tam značka, dokud se kamera nedotočí.
 */
export interface AimUiState {
  aimId: string;
  target: { u: number; v: number } | null;
  phase: AimProgress["phase"];
  iteration: number;
  result?: AimProgress["result"];
}

interface FoscamStore {
  /** slug → schopnosti kamery. */
  caps: Record<string, FoscamCaps>;
  /** slug → seznam presetů (lazy). */
  presets: Record<string, string[]>;
  /** slug → probíhající (nebo právě doběhlé) míření. */
  aim: Record<string, AimUiState | undefined>;
  setCaps: (list: FoscamCaps[]) => void;
  setPresets: (slug: string, presets: string[]) => void;
  beginAim: (slug: string, aimId: string, target: { u: number; v: number }) => void;
  applyAimProgress: (p: AimProgress) => void;
  clearAim: (slug: string) => void;
  fetchCaps: () => Promise<void>;
}

export const useFoscamStore = create<FoscamStore>((set) => ({
  caps: {},
  presets: {},
  aim: {},

  setCaps: (list) => set({ caps: Object.fromEntries(list.map((c) => [c.slug, c])) }),

  setPresets: (slug, presets) => set((s) => ({ presets: { ...s.presets, [slug]: presets } })),

  beginAim: (slug, aimId, target) =>
    set((s) => ({ aim: { ...s.aim, [slug]: { aimId, target, phase: "moving", iteration: 0 } } })),

  applyAimProgress: (p) =>
    set((s) => {
      const prev = s.aim[p.slug];
      // Cizí míření (jiný uživatel) taky zajímá — kamera se fyzicky hýbe všem. Značka
      // se ale kreslí jen u toho, kdo klikl, proto se `target` z předchozího stavu
      // přebírá jen při shodě aimId.
      const target = prev?.aimId === p.aimId ? (prev.target ?? null) : null;
      return {
        aim: {
          ...s.aim,
          [p.slug]: { aimId: p.aimId, target, phase: p.phase, iteration: p.iteration, result: p.result },
        },
      };
    }),

  clearAim: (slug) =>
    set((s) => {
      const next = { ...s.aim };
      delete next[slug];
      return { aim: next };
    }),

  fetchCaps: async () => {
    try {
      const res = await getFoscamCaps();
      const list = res.responseObject ?? [];
      set({ caps: Object.fromEntries(list.map((c) => [c.slug, c])) });
    } catch {
      /* offline / neautorizováno — necháme prázdné, ovládání se prostě neukáže */
    }
  },
}));
